import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supplierOrderApi } from '@/api/supplier'
import type { OrderListParams, DeliveryParams, SplitDeliveryParams } from '@/schemas/supplier'

// Query Keys
export const supplierOrderKeys = {
  all: ['supplier', 'orders'] as const,
  lists: () => [...supplierOrderKeys.all, 'list'] as const,
  list: (params: OrderListParams) => [...supplierOrderKeys.lists(), params] as const,
  details: () => [...supplierOrderKeys.all, 'detail'] as const,
  detail: (id: number) => [...supplierOrderKeys.details(), id] as const,
  statusList: (id: number) => [...supplierOrderKeys.details(), id, 'status'] as const,
}

/**
 * 获取订单列表
 */
export function useSupplierOrders(params: OrderListParams) {
  return useQuery({
    queryKey: supplierOrderKeys.list(params),
    queryFn: () => supplierOrderApi.list(params),
  })
}

/**
 * 获取订单详情
 */
export function useSupplierOrder(id: number, enabled = true) {
  return useQuery({
    queryKey: supplierOrderKeys.detail(id),
    queryFn: () => supplierOrderApi.detail(id),
    enabled: enabled && id > 0,
  })
}

/**
 * 获取订单状态记录
 */
export function useSupplierOrderStatus(id: number, enabled = true) {
  return useQuery({
    queryKey: supplierOrderKeys.statusList(id),
    queryFn: () => supplierOrderApi.statusList(id),
    enabled: enabled && id > 0,
  })
}

/**
 * 订单发货
 */
export function useOrderDelivery() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: DeliveryParams }) =>
      supplierOrderApi.delivery(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierOrderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: supplierOrderKeys.details() })
      toast.success('发货成功')
    },
  })
}

/**
 * 拆单发货
 */
export function useOrderSplitDelivery() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SplitDeliveryParams }) =>
      supplierOrderApi.splitDelivery(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierOrderKeys.lists() })
      queryClient.invalidateQueries({ queryKey: supplierOrderKeys.details() })
      toast.success('拆单发货成功')
    },
  })
}

/**
 * 刷新订单列表
 */
export function useSupplierOrderListRefresh() {
  const queryClient = useQueryClient()

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: supplierOrderKeys.lists() })
  }

  return { refresh }
}
